import { Injectable } from '@angular/core';
import { FolderLayout } from 'src/app/models/folder-layout';
import { IFolderLayout } from 'src/app/models/interfaces/folder-layout.interface';
import { ViewType } from 'src/app/models/server/enums/view-type';
import { View } from 'src/app/models/server/view';
import { ConnectedUsersStateService } from './connected-users-state.service';
import { ProfileSettingsStateService } from './profile-settings-state.service';

/**
 * Service de gestion de la disposition des sections du dossier.
 */
@Injectable({
    providedIn: 'root',
})
export class FolderLayoutsStateService {
    /**
     * Disposition par défaut des sections du dossier pour une vue de type bannettes.
     */
    public defaultBoxViewLayout: IFolderLayout = {
        nonDisplayedSections: [
            {
                name: 'Rappels',
                size: 25,
                resizingStatus: 2,
            },
        ],
        sectionsGroups: [
            {
                size: 50,
                visible: true,
                sections: [
                    {
                        name: 'Documents',
                        size: 50,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Attributs',
                        size: 50,
                        resizingStatus: 2,
                    },
                ],
            },
            {
                size: 50,
                visible: true,
                sections: [
                    {
                        name: 'Historique',
                        size: 50,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Visualisateur',
                        size: 100,
                        resizingStatus: 2,
                    },
                ],
            },
        ],
    };

    /**
     * Disposition par défaut des sections du dossier pour une vue de type workflow lorsque les sections sont divisées en 2 groupes.
     */
    public twoGroupsWorkflowViewLayout: IFolderLayout = {
        nonDisplayedSections: [
            {
                name: 'Rappels',
                size: 25,
                resizingStatus: 2,
            },
        ],
        sectionsGroups: [
            {
                size: 50,
                visible: true,
                sections: [
                    {
                        name: 'Documents',
                        size: 30,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Workflow',
                        size: 30,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Attributs',
                        size: 40,
                        resizingStatus: 2,
                    },
                ],
            },
            {
                size: 50,
                visible: true,
                sections: [
                    {
                        name: 'Historique',
                        size: 50,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Visualisateur',
                        size: 50,
                        resizingStatus: 2,
                    },
                ],
            },
        ],
    };

    /**
     * Disposition par défaut des sections du dossier pour une vue de type workflow lorsque les sections sont divisées en 3 groupes
     */
    public threeGroupsWorkflowViewLayout: IFolderLayout = {
        nonDisplayedSections: [
            {
                name: 'Rappels',
                size: 25,
                resizingStatus: 2,
            },
        ],
        sectionsGroups: [
            {
                size: 30,
                visible: true,
                sections: [
                    {
                        name: 'Workflow',
                        size: 50,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Attributs',
                        size: 50,
                        resizingStatus: 2,
                    },
                ],
            },
            {
                size: 30,
                visible: true,
                sections: [
                    {
                        name: 'Documents',
                        size: 50,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Historique',
                        size: 50,
                        resizingStatus: 2,
                    },
                ],
            },
            {
                size: 40,
                visible: true,
                sections: [
                    {
                        name: 'Visualisateur',
                        size: 100,
                        resizingStatus: 2,
                    },
                ],
            },
        ],
    };

    /**
     * Disposition par défaut des sections du dossier pour une vue de type workflow lorsque les sections sont divisées en 4 groupes
     */
    public fourGroupsWorkflowViewLayout: IFolderLayout = {
        nonDisplayedSections: [
            {
                name: 'Rappels',
                size: 25,
                resizingStatus: 2,
            },
        ],
        sectionsGroups: [
            {
                size: 25,
                visible: true,
                sections: [
                    {
                        name: 'Workflow',
                        size: 50,
                        resizingStatus: 2,
                    },
                    {
                        name: 'Attributs',
                        size: 50,
                        resizingStatus: 2,
                    },
                ],
            },
            {
                size: 25,
                visible: true,
                sections: [
                    {
                        name: 'Documents',
                        size: 100,
                        resizingStatus: 2,
                    },
                ],
            },
            {
                size: 25,
                visible: true,
                sections: [
                    {
                        name: 'Visualisateur',
                        size: 100,
                        resizingStatus: 2,
                    },
                ],
            },
            {
                size: 25,
                visible: true,
                sections: [
                    {
                        name: 'Historique',
                        size: 100,
                        resizingStatus: 2,
                    },
                ],
            },
        ],
    };

    /**
     * Constructeur.
     * @param connectedUserStateService : Service de gestion de l'utilisateur connecté.
     * @param profileSettingsStateService : Service de gestion des paramètres de profil.
     */
    public constructor(
        private connectedUserStateService: ConnectedUsersStateService,
        private profileSettingsStateService: ProfileSettingsStateService
    ) {}

    /**
     * Récupère la disposition des sections du dossier d'une vue.
     * @param folderViewId Vue à laquelle appartient le dossier.
     * @returns La disposition des sections du dossier.
     */
    public getfolderLayout(folderView: View): FolderLayout {
        var settings = this.profileSettingsStateService.getProfileSettings();
        var sectionsGroupsCount = settings.viewSettings.get(folderView.id) ? settings.viewSettings.get(folderView.id).sectionsGroupsCount : 2;
        let localStorageValue = localStorage.getItem(this.getLocalStorageKey(folderView.id, sectionsGroupsCount));

        if (localStorageValue) {
            return FolderLayout.from(JSON.parse(localStorageValue));
        } else {
            return this.getDefaultFolderLayout(folderView.viewType, sectionsGroupsCount);
        }
    }

    /**
     * Spécifie la disposition des sections du dossier d'une vue.
     * @param folderViewId Id de la vue à laquelle appartient le dossier.
     * @param folderLayout Instance de la disposition des sections du dossier.
     */
    public setFolderLayout(folderViewId: number, folderLayout: FolderLayout): void {
        var settings = this.profileSettingsStateService.getProfileSettings();

        localStorage.setItem(this.getLocalStorageKey(folderViewId, settings.viewSettings.get(folderViewId).sectionsGroupsCount), JSON.stringify(folderLayout));
    }

    /**
     * Récupère la clé utilisée pour stockée la disposition d'une vue dans le local storage pour un nombre de groupes de sections donné.
     * @param viewId : Id de la vue.
     * @param sectionGroupCount : Nombre de groupes de sections
     * @returns : La clé de stockage de la disposition dans le local storage.
     */
    private getLocalStorageKey(viewId: number, sectionGroupCount: number) {
        return `Folder-Layout-${this.connectedUserStateService.getUserConnected().userId}-${viewId}-${sectionGroupCount}`;
    }

    /**
     * Récupère la disposition des sections d'un dossier par défaut pour un type de vue et un nombre de groupes de sections donné.
     * @param viewType : Type de vue.
     * @param sectionGroupCount : Nombre de groupes de sections
     * @returns La disposition par défaut pour un type de vue et un nombre de groupes de section donné.
     */
    private getDefaultFolderLayout(viewType: ViewType, sectionGroupCount: number): FolderLayout {
        let defaultFolderLayout = new FolderLayout(sectionGroupCount);

        let sections = null;

        if (viewType === ViewType.Workflow || viewType === ViewType.ArchivingWorkflow) {
            if (sectionGroupCount === 2) {
                return FolderLayout.from(this.twoGroupsWorkflowViewLayout);
            } else if (sectionGroupCount === 3) {
                return FolderLayout.from(this.threeGroupsWorkflowViewLayout);
            } else if (sectionGroupCount === 4) {
                return FolderLayout.from(this.fourGroupsWorkflowViewLayout);
            } else {
                return FolderLayout.from(this.twoGroupsWorkflowViewLayout);
            }
        } else {
            return FolderLayout.from(this.defaultBoxViewLayout);
        }
    }
}
